/**
 * KJAR Modern Product Card
 * - Image carousel with dots
 * - Half-and-half colour circles
 * - Wishlist heart (Wishlist by Square compatible)
 * - "View Similar" product modal
 */
(function () {
  /**
   * Initialize all modern product cards on the page
   */
  function initProductCards() {
    document.querySelectorAll('.kjar-product-card').forEach(initSingleCard);
  }

  /**
   * Initialize a single card's carousel
   * @param {HTMLElement} cardEl
   */
  function initSingleCard(cardEl) {
    if (cardEl.dataset.kjarInit === 'true') return;
    cardEl.dataset.kjarInit = 'true';

    const track = cardEl.querySelector('.kjar-card__carousel-track');
    const dotsContainer = cardEl.querySelector('.kjar-card__dots');
    const slides = cardEl.querySelectorAll('.kjar-card__slide');

    if (!track || slides.length <= 1) {
      // Hide dots if only 1 image
      if (dotsContainer) dotsContainer.style.display = 'none';
      return;
    }

    let currentIndex = 0;
    let interval = null;

    // Create dots
    slides.forEach((_, i) => {
      const dot = document.createElement('button');
      dot.className = 'kjar-card__dot';
      dot.setAttribute('aria-label', `Image ${i + 1}`);
      dot.addEventListener('click', () => goToSlide(i));
      if (i === 0) dot.classList.add('kjar-card__dot--active');
      dotsContainer.appendChild(dot);
    });

    const allDots = dotsContainer.querySelectorAll('.kjar-card__dot');

    function goToSlide(index) {
      currentIndex = index;
      track.style.transform = `translateX(-${currentIndex * 100}%)`;
      allDots.forEach((d, i) => d.classList.toggle('kjar-card__dot--active', i === currentIndex));
    }

    function nextSlide() {
      goToSlide((currentIndex + 1) % slides.length);
    }

    function startAutoplay() {
      stopAutoplay();
      interval = setInterval(nextSlide, 3000);
    }

    function stopAutoplay() {
      if (interval) {
        clearInterval(interval);
        interval = null;
      }
    }

    // Autoplay
    startAutoplay();

    // Pause on hover
    cardEl.addEventListener('mouseenter', stopAutoplay);
    cardEl.addEventListener('mouseleave', startAutoplay);

    // Touch swipe support
    let touchStartX = 0;
    let touchEndX = 0;

    track.addEventListener('touchstart', (e) => {
      touchStartX = e.changedTouches[0].screenX;
      stopAutoplay();
    }, { passive: true });

    track.addEventListener('touchend', (e) => {
      touchEndX = e.changedTouches[0].screenX;
      const diff = touchStartX - touchEndX;
      if (Math.abs(diff) > 50) {
        if (diff > 0) {
          goToSlide((currentIndex + 1) % slides.length);
        } else {
          goToSlide((currentIndex - 1 + slides.length) % slides.length);
        }
      }
      startAutoplay();
    });
  }

  /**
   * Initialize wishlist hearts
   */
  function initWishlistButtons() {
    // Check if Wishlist by Square app is loaded
    // The app typically creates a global 'Swym' or 'SquareWishlist' object
    // We use a heart button that the app can hook into via data attributes

    document.querySelectorAll('.kjar-card__wishlist').forEach((btn) => {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();

        const productId = this.getAttribute('data-product-id');
        const heartIcon = this.querySelector('.kjar-card__heart');

        // Try Square Wishlist / Swym integration
        if (typeof window._swat !== 'undefined' && window._swat.dispatch) {
          // Swym wishlist
          window._swat.dispatch('addToWishlist', { epi: productId });
          heartIcon.classList.toggle('kjar-card__heart--active');
          return;
        }

        if (typeof window.SwymCallbacks !== 'undefined') {
          window._swat.addToWishList({ epi: productId }, () => {
            heartIcon.classList.toggle('kjar-card__heart--active');
          });
          return;
        }

        // Fallback: localStorage-based wishlist
        toggleLocalWishlist(productId, heartIcon);
      });
    });

    // Set initial active state from localStorage
    document.querySelectorAll('.kjar-card__wishlist').forEach((btn) => {
      const productId = btn.getAttribute('data-product-id');
      const heartIcon = btn.querySelector('.kjar-card__heart');
      if (isInWishlist(productId)) {
        heartIcon.classList.add('kjar-card__heart--active');
      }
    });
  }

  function toggleLocalWishlist(productId, heartIcon) {
    const wishlist = getLocalWishlist();
    const idx = wishlist.indexOf(productId);
    if (idx > -1) {
      wishlist.splice(idx, 1);
      heartIcon.classList.remove('kjar-card__heart--active');
    } else {
      wishlist.push(productId);
      heartIcon.classList.add('kjar-card__heart--active');
    }
    localStorage.setItem('kjar_wishlist', JSON.stringify(wishlist));
  }

  function isInWishlist(productId) {
    return getLocalWishlist().includes(productId);
  }

  function getLocalWishlist() {
    try {
      return JSON.parse(localStorage.getItem('kjar_wishlist') || '[]');
    } catch {
      return [];
    }
  }

  /**
   * Initialize "View Similar" buttons
   */
  function initViewSimilarButtons() {
    document.querySelectorAll('.kjar-card__view-similar').forEach((btn) => {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();

        const productHandle = this.getAttribute('data-product-handle');
        const productType = this.getAttribute('data-product-type');
        const productTags = this.getAttribute('data-product-tags');

        openSimilarModal(productHandle, productType, productTags);
      });
    });
  }

  /**
   * Opens a modal showing similar products
   */
  function openSimilarModal(productHandle, productType, productTags) {
    // Remove existing modal
    const existing = document.querySelector('.kjar-similar-modal');
    if (existing) existing.remove();

    const modal = document.createElement('div');
    modal.className = 'kjar-similar-modal';
    modal.innerHTML = `
      <div class="kjar-similar-modal__overlay"></div>
      <div class="kjar-similar-modal__content">
        <button class="kjar-similar-modal__close" aria-label="Close">&times;</button>
        <h3 class="kjar-similar-modal__title">Similar Products</h3>
        <div class="kjar-similar-modal__grid">
          <div class="kjar-similar-modal__loading">Loading similar products...</div>
        </div>
      </div>
    `;

    document.body.appendChild(modal);
    document.body.style.overflow = 'hidden';

    // Close handlers
    const closeBtn = modal.querySelector('.kjar-similar-modal__close');
    const overlay = modal.querySelector('.kjar-similar-modal__overlay');

    function closeModal() {
      modal.remove();
      document.body.style.overflow = '';
    }

    closeBtn.addEventListener('click', closeModal);
    overlay.addEventListener('click', closeModal);
    document.addEventListener('keydown', function escHandler(e) {
      if (e.key === 'Escape') {
        closeModal();
        document.removeEventListener('keydown', escHandler);
      }
    });

    // Fetch similar products via predictive search or search
    const searchTerm = productType || (productTags ? productTags.split(',')[0].trim() : '');
    if (!searchTerm) {
      modal.querySelector('.kjar-similar-modal__loading').textContent = 'No similar products found.';
      return;
    }

    const searchUrl = `${window.Theme?.routes?.search_url || '/search'}?q=${encodeURIComponent(searchTerm)}&type=product&section_id=predictive-search`;

    fetch(searchUrl)
      .then((response) => response.text())
      .then((html) => {
        const grid = modal.querySelector('.kjar-similar-modal__grid');
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        // Extract product cards from the predictive search results
        const productItems = doc.querySelectorAll('.predictive-search-results__list-item, resource-card, product-card');
        let productHTML = '';

        if (productItems.length === 0) {
          grid.innerHTML = '<p class="kjar-similar-modal__empty">No similar products found.</p>';
          return;
        }

        productItems.forEach((item) => {
          const link = item.querySelector('a[href]');
          const img = item.querySelector('img');
          const title = item.querySelector('.product-title, h3, h4, .h4, .h5');
          const price = item.querySelector('.price, product-price');

          if (!link) return;

          // Skip the current product
          if (link.getAttribute('href') && link.getAttribute('href').includes(productHandle)) return;

          productHTML += `
            <a href="${link.getAttribute('href')}" class="kjar-similar-modal__card">
              <div class="kjar-similar-modal__image">
                ${img ? img.outerHTML : '<div class="kjar-similar-modal__placeholder"></div>'}
              </div>
              <div class="kjar-similar-modal__info">
                <p class="kjar-similar-modal__product-title">${title ? title.textContent.trim() : ''}</p>
                <div class="kjar-similar-modal__price">${price ? price.outerHTML : ''}</div>
              </div>
            </a>
          `;
        });

        grid.innerHTML = productHTML || '<p class="kjar-similar-modal__empty">No similar products found.</p>';
      })
      .catch(() => {
        modal.querySelector('.kjar-similar-modal__loading').textContent = 'Could not load similar products.';
      });
  }

  /**
   * Refresh Judge.me preview badges
   * Judge.me's JS scans for .jdgm-preview-badge elements
   */
  function refreshJudgeMe() {
    document.querySelectorAll('.jdgm-preview-badge[data-id]').forEach(function(badge) {
      if (badge.children.length > 0 && badge.querySelector('.jdgm-star')) return;
      badge.innerHTML = '<span class="jdgm-preview-badge__stars" style="font-size:11px;color:#9E9E9E;">★★★★★</span> <span class="jdgm-preview-badge__text" style="font-size:10px;color:#9E9E9E;">No reviews</span>';
    });
    if (typeof jdgm !== 'undefined' && typeof jdgm.customizeBadges === 'function') {
      jdgm.customizeBadges();
    }
  }

  // Initialize on DOM ready
  function init() {
    initProductCards();
    initWishlistButtons();
    initViewSimilarButtons();
    setTimeout(refreshJudgeMe, 500);
    setTimeout(refreshJudgeMe, 1500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-initialize on Shopify section load/update
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:reorder', init);
})();